import * as React from 'react'
import { Download, FileDown } from 'lucide-react'
import { useDataExports, useCreateDataExport, useDataExportDownloadUrl } from '@/lib/queries.js'
import { Button } from '@/components/ui/button.jsx'
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/card.jsx'
import { Alert, Spinner } from '@/components/ui/feedback.jsx'
import { Badge } from '@/components/ui/badge.jsx'
import { humanMessage } from '@/lib/errors.js'
import { humanise } from '@/lib/format.js'

/**
 * Data export (Phase 2, item 21 — D28e). Commitment 3 of the published
 * portability guarantee: "Data export is standard and free on departure" —
 * this is that promise, kept.
 */

const EXPORT_TYPES = [
  { key: 'clients', label: 'Clients' },
  { key: 'jobs', label: 'Jobs' },
  { key: 'invoices', label: 'Invoices' }
]

const STATUS_TONE = { complete: 'success', processing: 'info', failed: 'danger', pending: 'neutral' }

export function DataExportCard () {
  const { data: exports, isPending, error } = useDataExports()
  const create = useCreateDataExport()
  const download = useDataExportDownloadUrl()
  const [localError, setLocalError] = React.useState(null)

  async function requestExport (type) {
    setLocalError(null)
    try { await create.mutateAsync(type) } catch (err) { setLocalError(err) }
  }

  async function openDownload (id) {
    setLocalError(null)
    try {
      const { url } = await download.mutateAsync(id)
      window.open(url, '_blank', 'noopener')
    } catch (err) { setLocalError(err) }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileDown className="size-4 text-ink-subtle" aria-hidden="true" />
          Export your data
        </CardTitle>
        <p className="mt-1 text-sm/relaxed text-ink-muted">
          Your clients, jobs and invoices, as CSV files you can open in any
          spreadsheet. Free, any time, no questions asked. Files expire after
          7 days.
        </p>
      </CardHeader>

      <CardBody className="space-y-4">
        {(error || localError) && <Alert tone="danger">{humanMessage(localError ?? error)}</Alert>}

        <div className="flex flex-wrap gap-2">
          {EXPORT_TYPES.map(t => (
            <Button
              key={t.key}
              variant="secondary"
              size="sm"
              loading={create.isPending}
              onClick={() => requestExport(t.key)}
              data-testid={`export-${t.key}`}
            >
              <Download className="size-3.5" aria-hidden="true" /> {t.label}
            </Button>
          ))}
        </div>

        {isPending
          ? <div className="flex justify-center py-4"><Spinner label="Loading exports" /></div>
          : exports?.length > 0 && (
            <ul className="space-y-2" data-testid="export-history">
              {exports.map(row => (
                <li
                  key={row.id}
                  className="flex min-h-tap items-center justify-between gap-3 rounded-md border border-line bg-surface-sunken px-3 py-2"
                >
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-ink">{EXPORT_TYPES.find(t => t.key === row.export_type)?.label ?? row.export_type}</span>
                    <Badge tone={STATUS_TONE[row.status] ?? 'neutral'}>{humanise(row.status)}</Badge>
                  </div>
                  {row.status === 'complete' && (
                    <Button variant="ghost" size="sm" onClick={() => openDownload(row.id)} loading={download.isPending}>
                      Download
                    </Button>
                  )}
                </li>
              ))}
            </ul>
            )}
      </CardBody>
    </Card>
  )
}
